'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

export default function LandingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(12px)' : 'none',
        backgroundColor: scrolled ? 'rgba(253, 250, 246, 0.92)' : 'transparent',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <AppLogo size={34} />
          <span className="font-serif text-lg" style={{ color: 'var(--foreground)' }}>
            ReviewReply AI
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: 'How it works', href: '#how-it-works' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'Try it free', href: '#demo' },
          ].map((item) => (
            <a
              key={`nav-${item.label}`}
              href={item.href}
              className="text-sm font-medium transition-colors duration-150"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) => ((e.target as HTMLElement).style.color = 'var(--primary)')}
              onMouseLeave={(e) => ((e.target as HTMLElement).style.color = 'var(--muted-foreground)')}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            href="/sign-up-login-screen"
            className="text-sm font-medium transition-colors duration-150"
            style={{ color: 'var(--muted-foreground)' }}
          >
            Sign in
          </Link>
          <Link
            href="/sign-up-login-screen"
            className="btn-primary text-sm px-5 py-2.5"
          >
            Start free
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle mobile menu"
          style={{ color: 'var(--foreground)' }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            {mobileOpen ? (
              <path fillRule="evenodd" clipRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            ) : (
              <path fillRule="evenodd" clipRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="md:hidden border-t px-6 py-4 space-y-3"
          style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
        >
          {['How it works', 'Pricing', 'Try it free'].map((item) => (
            <a
              key={`mobile-${item}`}
              href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
              className="block text-sm font-medium py-2"
              style={{ color: 'var(--muted-foreground)' }}
              onClick={() => setMobileOpen(false)}
            >
              {item}
            </a>
          ))}
          <Link
            href="/sign-up-login-screen"
            className="btn-primary block text-center text-sm px-5 py-2.5 mt-2"
          >
            Start free
          </Link>
        </div>
      )}
    </header>
  );
}